import type { Result } from '@avalara/types/index';

import type { RouteComponentProps } from 'react-router-dom';
import conf from '../../conf';
import { isEmbeddedSelector } from '../../selectors/offer';
import { selectShouldRedirectAfterPurchase } from '../../selectors/redirect';
import { selectPlan, setConnectorValidationStatus } from '../../store/order';
import { setReadOnlyField } from '../../store/shared-signup';
import fetch from '../fetch';
import { getWindowQueryUnlessInProd } from '../helpers';
import { selectConnector } from './connectors';
import { getSystemErrorMessaging } from './error';
import { redirectAfterPurchase } from './redirect';

import type { Dispatch, ThunkAction } from '@avalara/types/actions';
import type { State } from '@avalara/types/reducer-states';
import { type OfferPlanClientDetails, Pages } from '../../../../types/offers';

export const validateAndSelectConnector =
  (connectorId: string | null) => async (dispatch: Dispatch, getState: () => State) => {
    const {
      data: { connectors },
      offer: { offerFlow, pages, usesConnectors },
    } = getState();
    // NOTE (Andrew) - we need the connectors in the state so that we can set the specified
    //                 connector in state in the case that it's valid:
    if (!usesConnectors || !connectors.data.length) return;

    if (!connectorId) {
      const expectsConnectorFromUrl = usesConnectors === 'REQUIRED' && !pages.includes(Pages.SelectConnector);
      if (expectsConnectorFromUrl) dispatch(getSystemErrorMessaging({ errorCode: 'missingConnector' }));
      return;
    }

    const query = getWindowQueryUnlessInProd();
    const result: Result<{ isValid: boolean }> = await fetch(dispatch)(
      `${conf.API_URL}/api/offers/${offerFlow}/validate-connector/${connectorId}${query}`,
    );

    if (!result.ok || !result.value.isValid) {
      // NOTE (Andrew) - if the connector isn't required for the flow, just fail silently and don't
      //                 set the specified connector
      // TODO (Andrew) - determine if we want custom handling for this:
      //                 right now it says they're ineligible for the offer, when in fact they're
      //                 really just ineligible for the specific connector
      if (usesConnectors === 'REQUIRED') dispatch(getSystemErrorMessaging({ errorCode: 'ineligibleConnector' }));
      return;
    }

    dispatch(selectConnector(connectorId));
    dispatch(setConnectorValidationStatus(true));
  };

const pushPageLocation = (pathname: string, history: RouteComponentProps['history']) => {
  const newLocation = {
    pathname,
    search: history.location.search,
  };
  history.push(newLocation);
};

const replacePageLocation = (pathname: string, history: RouteComponentProps['history']) => {
  const newLocation = {
    pathname,
    search: history.location.search,
  };
  history.replace(newLocation);
};

const getCurrentPageIndex = (pages: Array<Pages>, currentPage: Pages | undefined, pathname: string): number => {
  const indexFromState = currentPage != null ? pages.indexOf(currentPage) : -1;
  if (indexFromState >= 0) return indexFromState;

  if (!pathname) return -1;

  return pages.findIndex((page) => pathname.endsWith(`/${page}`));
};

type GoToNextOfferStepOptions = { shouldReplaceHistory?: boolean; history: RouteComponentProps['history'] };
export const goToNextOfferStep =
  ({ history, shouldReplaceHistory }: GoToNextOfferStepOptions): ThunkAction =>
  (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const {
      offer: { currentPage, offerFlow, pages },
    } = state;

    const setPageLocation = shouldReplaceHistory ? replacePageLocation : pushPageLocation;
    if (!offerFlow || pages.length === 0) return;

    const currentPageIndex = getCurrentPageIndex(pages, currentPage, history.location.pathname);
    if (currentPageIndex < 0) {
      setPageLocation(`/${offerFlow}/${pages[0]}`, history);
      return;
    }

    if (pages[currentPageIndex + 1] === Pages.Redirect) {
      if (selectShouldRedirectAfterPurchase(state)) {
        dispatch(redirectAfterPurchase());
      } else {
        setPageLocation(`/${offerFlow}/thank-you`, history);
      }
    } else {
      setPageLocation(`/${offerFlow}/${pages[currentPageIndex + 1]}`, history);
    }
  };

export const completePlanSelect =
  (plan: OfferPlanClientDetails, options: GoToNextOfferStepOptions): ThunkAction =>
  (dispatch: Dispatch) => {
    dispatch(selectPlan({ planName: plan.name, planSelectComplete: true }));
    dispatch(goToNextOfferStep(options));
    if (plan.planCountry) dispatch(setReadOnlyField({ field: 'country', value: plan.planCountry }));
  };

// Note(Marlene): The back button is only used for embedded offer flows.
export const goToPreviousOfferStep =
  (history: RouteComponentProps['history']): ThunkAction =>
  (_: Dispatch, getState: () => State) => {
    const state = getState();
    const {
      offer: { currentPage, offerFlow },
    } = state;
    const isEmbedded = isEmbeddedSelector(state);
    if (!offerFlow) return;

    const query = history.location.search;

    // Embedded flows get stuck going back from payment when adding a new card (use needs to press back button 3x because extra history
    // entries from Iframe on chrome based browsers). This is a workaround for those cases: directly navigate to previous offer state
    if (currentPage === 'payment' && isEmbedded) {
      history.replace(`/${offerFlow}${query}`);
    } else {
      history.goBack();
    }
  };
